/*
 * Customer-facing copy for the eight audit verdicts (U25).
 *
 * The status metadata module (`src/lib/status.ts`) owns each verdict's *tone* and
 * short *label* — those drive the colour-coded `StatusBadge`. This module adds
 * the report-only narrative layer: a one-line plain-English summary of "what
 * this verdict means", and whether the verdict is a discrepancy that warrants
 * a recovery action (links to the run-detail `needs_human` items).
 *
 * Keyed by the closed 8-verdict enum (`AUDIT_VERDICTS` from `lib/status`),
 * so this stays exhaustive — adding a ninth verdict upstream is a compile error
 * here. Lookups are case/separator tolerant and fall back gracefully for any
 * unrecognised classification string the server might send.
 */

import { AUDIT_VERDICTS, type AuditVerdict } from "../../lib/status";

/** Does a verdict represent a discrepancy that needs a human/recovery action? */
export interface VerdictCopy {
  /** One-line plain-English summary shown in the group header. */
  summary: string;
  /**
   * Whether this verdict surfaces a recovery action on the run-detail screen
   * (the `needs_human` items). `verified_ok` is clean; the rest carry a next
   * step the operator can act on.
   */
  actionable: boolean;
}

const VERDICT_COPY: Record<AuditVerdict, VerdictCopy> = {
  verified_ok: {
    summary:
      "Live on Meta with the expected ads — independently confirmed against the platform.",
    actionable: false,
  },
  count_mismatch: {
    summary:
      "The adset is live but has fewer ads than expected — a partial launch that needs a manual delete-and-rerun.",
    actionable: true,
  },
  name_mismatch: {
    summary:
      "An adset was found but its ad names don't match this batch — wrong-batch ads may be present. Inspect before activating.",
    actionable: true,
  },
  ads_disapproved: {
    summary:
      "Ads launched but Meta disapproved or flagged one or more — review the policy violation, fix the creative, and relaunch.",
    actionable: true,
  },
  adset_not_found: {
    summary:
      "No adset with the expected name exists on Meta — the launch did not land. Re-finalize this concept, then re-audit.",
    actionable: true,
  },
  claimed_failure_but_launched: {
    summary:
      "The launch reported a failure, but the adset is actually live with the full ad count. No action — just bookkeep it as launched.",
    actionable: true,
  },
  failed_confirmed: {
    summary:
      "The launch failed and Meta confirms nothing landed — review the recorded failure reason before retrying.",
    actionable: true,
  },
  unverifiable: {
    summary:
      "Meta could not be reached for this ticket, so the outcome is unknown — re-run the audit to resolve it.",
    actionable: true,
  },
};

/** Normalise a backend classification string to the canonical verdict key. */
function normalizeVerdict(classification: string): string {
  return classification.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

/** Type guard: is `value` one of the eight known verdicts? */
export function isAuditVerdict(value: string): value is AuditVerdict {
  return (AUDIT_VERDICTS as readonly string[]).includes(value);
}

/**
 * Customer-facing copy for a verdict string (case/separator tolerant). Falls
 * back to a neutral, actionable summary for any unrecognised classification so
 * a new server enum value still renders a sane, surfaced-for-review row.
 */
export function verdictCopy(classification: string): VerdictCopy {
  const key = normalizeVerdict(classification);
  if (isAuditVerdict(key)) return VERDICT_COPY[key];
  return {
    summary:
      "An unrecognised audit verdict — surfaced for review rather than acted on automatically.",
    actionable: true,
  };
}

/** Stable display order for verdict groups: clean first, then by severity. */
export const VERDICT_DISPLAY_ORDER: readonly AuditVerdict[] = [
  "verified_ok",
  "claimed_failure_but_launched",
  "adset_not_found",
  "count_mismatch",
  "name_mismatch",
  "ads_disapproved",
  "failed_confirmed",
  "unverifiable",
];

/**
 * Sort key for an arbitrary classification: known verdicts sort by the display
 * order above; anything unrecognised sorts last (but before nothing).
 */
export function verdictOrderIndex(classification: string): number {
  const key = normalizeVerdict(classification);
  const idx = isAuditVerdict(key)
    ? VERDICT_DISPLAY_ORDER.indexOf(key)
    : -1;
  return idx === -1 ? VERDICT_DISPLAY_ORDER.length : idx;
}
